// Schemi Zod condivisi per API esterna v1, webhook, GDPR, kiosk (Sprint 7).
import { z } from 'zod';
// ============================================================================
// API keys
// ============================================================================
export const API_KEY_SCOPES = [
    'encounters:read',
    'identities:read',
    'flows:read',
    'webhooks:manage',
];
export const ApiKeyScopeSchema = z.enum(API_KEY_SCOPES);
export const ApiKeyCreateSchema = z.object({
    name: z.string().trim().min(1).max(120),
    scopes: z.array(ApiKeyScopeSchema).min(1),
    rate_limit_per_minute: z.number().int().min(1).max(10_000).optional(),
    expires_at: z.string().datetime().nullable().optional(),
});
export const ApiKeyListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    key_prefix: z.string(),
    scopes: z.array(ApiKeyScopeSchema),
    rate_limit_per_minute: z.number().int(),
    last_used_at: z.string().datetime().nullable(),
    expires_at: z.string().datetime().nullable(),
    revoked_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    status: z.enum(['active', 'revoked', 'expired']),
});
export const ApiKeyCreateResponseSchema = z.object({
    ok: z.literal(true),
    api_key: ApiKeyListItemSchema,
    // Mostrata UNA SOLA VOLTA, non recuperabile dopo.
    raw_key: z.string(),
});
// ============================================================================
// Webhooks
// ============================================================================
export const WEBHOOK_EVENTS = [
    'encounter.completed',
    'encounter.aborted_emergency',
    'encounter.abandoned',
    'test.ping',
];
export const WebhookEventSchema = z.enum(WEBHOOK_EVENTS);
export const WebhookCreateSchema = z.object({
    name: z.string().trim().min(1).max(120),
    url: z
        .string()
        .url()
        .refine((u) => /^https?:\/\//.test(u), { message: 'URL deve iniziare con http:// o https://' }),
    events: z.array(WebhookEventSchema).min(1),
    // Se non fornito, generato server-side e ritornato una volta sola.
    secret: z.string().min(16).max(256).optional(),
});
export const WebhookUpdateSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    events: z.array(WebhookEventSchema).min(1).optional(),
    is_active: z.boolean().optional(),
});
export const WebhookListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    url: z.string(),
    events: z.array(WebhookEventSchema),
    is_active: z.boolean(),
    created_at: z.string().datetime(),
    last_delivery_at: z.string().datetime().nullable().optional(),
    last_delivery_status: z.string().nullable().optional(),
    pending_count: z.number().int().nullable().optional(),
});
export const WebhookCreateResponseSchema = z.object({
    ok: z.literal(true),
    webhook: WebhookListItemSchema,
    raw_secret: z.string(),
});
export const WebhookDeliveryStatusSchema = z.enum(['pending', 'delivered', 'failed', 'exhausted']);
export const WebhookDeliveryRowSchema = z.object({
    id: z.string().uuid(),
    webhook_id: z.string().uuid(),
    event_type: z.string(),
    encounter_id: z.string().uuid().nullable(),
    status: WebhookDeliveryStatusSchema,
    attempts: z.number().int(),
    last_attempt_at: z.string().datetime().nullable(),
    next_attempt_at: z.string().datetime().nullable(),
    response_status: z.number().int().nullable(),
    response_body_excerpt: z.string().nullable(),
    delivered_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
});
// ============================================================================
// GDPR cittadino
// ============================================================================
export const GdprIdentifierSchema = z
    .object({
    pseudonym: z.string().min(4).max(80).optional(),
    pickup_code: z.string().min(4).max(20).optional(),
})
    .refine((v) => Boolean(v.pseudonym || v.pickup_code), {
    message: 'pseudonym o pickup_code richiesto',
});
export const GdprDeleteSchema = GdprIdentifierSchema.and(z.object({ confirm: z.literal(true) }));
// ============================================================================
// Kiosk settings
// ============================================================================
export const KioskSettingsUpdateSchema = z.object({
    kiosk_mode_enabled: z.boolean().optional(),
    // PIN 4-8 cifre. NULL/empty per rimuoverlo.
    kiosk_exit_pin: z
        .string()
        .regex(/^\d{4,8}$/)
        .nullable()
        .optional(),
    kiosk_idle_reset_seconds: z.number().int().min(10).max(120).optional(),
    kiosk_welcome_message: z.string().max(280).nullable().optional(),
});
// ============================================================================
// Retention
// ============================================================================
export const RetentionExecutionRowSchema = z.object({
    id: z.string().uuid(),
    executed_at: z.string().datetime(),
    encounters_deleted: z.number().int(),
    encounters_skipped_clinical: z.number().int(),
    test_sessions_deleted: z.number().int(),
    duration_ms: z.number().int(),
    triggered_by: z.enum(['cron', 'manual', 'gdpr_request']),
});
// ============================================================================
// Pagination helper
// ============================================================================
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    per_page: z.coerce.number().int().min(1).max(200).default(20),
});
//# sourceMappingURL=integrations.js.map